import { createSelector } from 'reselect'
import { isCruiseV1, isFlightItem, isInstantBookingHotelItem, isInsuranceItem, isInsuranceProtectionItem, isNoProtectionInsuranceBookingProtectionItem, isNoProtectionInsuranceItem } from 'lib/checkout/checkoutUtils'
import { findInsuranceUpgradeSourceItem } from 'lib/insurance/insuranceHelpers'
import { reselectShallowEqual } from 'lib/selector/selectorUtils'
import { getAccommodationItems } from './accommodation'
import { getFlightItems } from './flights'
import { isEnabledForFeature } from 'lib/config/featureFlagUtils'
import { CHECKOUT_ITEM_TYPE_CRUISE, CHECKOUT_ITEM_TYPE_TOUR_V1 } from 'constants/checkout'
import config from 'constants/config'
import getBedbankItems from './items/getBedbankItems'
import getAllInsuranceBookingProtectionItems from './items/getAllInsuranceBookingProtectionItems'
import { isPassportCardInsuranceItem } from 'checkout/Components/CheckoutSteps/Review/PassportCardInsurance/passportCardUtils'
import {
  INSURANCE_TYPE_PASSPORT_CARD,
  PRODUCT_ID_NO_PROTECTION,
  PRODUCT_ID_PASSPORT_CARD,
} from 'constants/insurance'

function isTravelInsuranceItem(item: App.Checkout.InsuranceItem): boolean {
  return item.insuranceType !== 'cfmr' && !isPassportCardInsuranceItem(item)
}

function isXcoverInsuranceItem(item: App.Checkout.InsuranceItem): boolean {
  return !isPassportCardInsuranceItem(item)
}

function isInsuranceItemForProductType(
  item: App.Checkout.InsuranceItem,
  productType: App.InsuranceProductType,
): boolean {
  if (productType === 'booking-protection') {
    return item.insuranceType === 'cfmr'
  }

  return isTravelInsuranceItem(item)
}

function isNoProtectionXCoverInsuranceItem(item: App.Checkout.InsuranceItem): boolean {
  return item.productId === PRODUCT_ID_NO_PROTECTION && isXcoverInsuranceItem(item)
}

function isNoProtectionPassportCardInsuranceItem(item: App.Checkout.InsuranceItem): boolean {
  return item.productId === PRODUCT_ID_NO_PROTECTION && item.insuranceType === INSURANCE_TYPE_PASSPORT_CARD
}

export const getInsuranceItems = createSelector(
  (state: App.State) => state.checkout.cart.items,
  (items): Array<App.Checkout.InsuranceItem> => items.filter(isInsuranceProtectionItem),
  reselectShallowEqual,
)

export function getInsuranceItemByProductType(
  state: App.State,
  productType: App.InsuranceProductType,
): App.Checkout.InsuranceItem | undefined {
  return getInsuranceItems(state).find(item => isInsuranceItemForProductType(item, productType))
}

export const getNoProtectionInsuranceItems = createSelector(
  (state: App.State) => state.checkout.cart.items,
  (items): Array<App.Checkout.InsuranceItem> => items.filter(isNoProtectionInsuranceItem),
  reselectShallowEqual,
)

export const getNoProtectionXCoverInsuranceItems = createSelector(
  (state: App.State) => state.checkout.cart.items,
  (items): Array<App.Checkout.InsuranceItem> => items
    .filter(isInsuranceItem)
    .filter(isNoProtectionXCoverInsuranceItem),
  reselectShallowEqual,
)

export const getNoProtectionPassportCardInsuranceItem = createSelector(
  (state: App.State) => state.checkout.cart.items,
  (items): App.Checkout.InsuranceItem | undefined => items
    .filter(isInsuranceItem)
    .find(isNoProtectionPassportCardInsuranceItem),
)

export const getNoProtectionInsuranceBookingProtectionItems = createSelector(
  (state: App.State) => state.checkout.cart.items,
  (items): Array<App.Checkout.InsuranceItem> => items.filter(isNoProtectionInsuranceBookingProtectionItem),
  reselectShallowEqual,
)

// It includes the no-protection insurance item
export const getAllInsuranceItems = createSelector(
  (state: App.State) => state.checkout.cart.items,
  (items): Array<App.Checkout.InsuranceItem> => items.filter(isInsuranceItem),
  reselectShallowEqual,
)

export const getAllPassportCardInsuranceItems = createSelector(
  (state: App.State) => getAllInsuranceItems(state),
  (items): Array<App.Checkout.InsuranceItem> => items.filter(isPassportCardInsuranceItem),
  reselectShallowEqual,
)

export const getPassportCardInsuranceItem = createSelector(
  (state: App.State) => getAllPassportCardInsuranceItems(state),
  (items): App.Checkout.InsuranceItem | undefined => items[0],
)

export const getSelectedPassportCardInsuranceItem = createSelector(
  (state: App.State) => getAllPassportCardInsuranceItems(state),
  (items): App.Checkout.InsuranceItem | undefined =>
    items.find(item => item.productId === PRODUCT_ID_PASSPORT_CARD) ??
    items.find(item => !!item.productId && item.productId !== PRODUCT_ID_NO_PROTECTION),
)

export const getAllTravelInsuranceItems = createSelector(
  (state: App.State) => getAllInsuranceItems(state),
  (items): Array<App.Checkout.InsuranceItem> => items.filter(isTravelInsuranceItem),
  reselectShallowEqual,
)

export const getAllXcoverInsuranceItems = createSelector(
  (state: App.State) => getAllInsuranceItems(state),
  (items): Array<App.Checkout.InsuranceItem> => items.filter(isXcoverInsuranceItem),
  reselectShallowEqual,
)

export const getInsuranceUpgradeSourceItem = createSelector(
  (state: App.State) => state.checkout.cart.existingOrder,
  (state: App.State) => state.checkout.cart.existingOrder?.insuranceItems,
  (existingOrder, insuranceItems) => {
    const activeInsuranceItem = insuranceItems?.find(item => item.status === 'completed')

    if (!activeInsuranceItem || !existingOrder) {
      return null
    }

    const insuranceUpgradeSourceItem = findInsuranceUpgradeSourceItem(activeInsuranceItem, existingOrder)
    return insuranceUpgradeSourceItem
  },
)

export const isCheckoutPostPurchaseInsurance = createSelector(
  (state: App.State) => state.checkout.cart.mode,
  (checkoutMode) => checkoutMode === 'insurance',
)

export const getInsuranceEnabledCheckoutItems = createSelector(
  (state: App.State) => getAccommodationItems(state),
  (state: App.State) => getFlightItems(state),
  (state: App.State) => state.offer.offers,
  (items, flighItems, leOffers): Array<App.Checkout.InstantBookingItem | App.Checkout.FlightItem> => {
    return [...items, ...flighItems]
      .filter(item => isInstantBookingHotelItem(item) || isFlightItem(item))
      .filter(item => isEnabledForFeature(config.UNIVERSAL_CHECKOUT_INSURANCE_ENABLED_LIST, item.itemType))
      .filter(item => {
        if (item.itemType === CHECKOUT_ITEM_TYPE_TOUR_V1 || item.itemType === CHECKOUT_ITEM_TYPE_CRUISE) {
          return isCruiseV1(item, leOffers)
        }
        return true
      })
  },
)

export const checkoutContainsBedbankWithCFMRFlexPackage = createSelector(
  (state: App.State) => getBedbankItems(state),
  (state: App.State) => getAllInsuranceBookingProtectionItems(state),
  (bedbankItems, bookingProtectionItems): boolean => {
    return bookingProtectionItems.some(item => !!item.bedbankRoomIds?.length || !!item.isAccommodationBundle)
  },
)
